// src/Pages/Admin/Sesion/FotoUploader.js
// Dropzone con drag & drop. La subida real vive en `utils/fotosService`.
import React, { useState, useRef } from "react";
import { Upload, Loader2 } from "lucide-react";
import { subirFotos } from "../../../utils/fotosService";

const EXTENSIONES = /\.(jpeg|jpg|png|gif|webp|bmp)$/i;

const FotoUploader = ({ sesionId, disabled, onSubidas }) => {
  const [arrastrando, setArrastrando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const habilitado = Boolean(sesionId) && !disabled && progreso === null;

  const procesar = async (fileList) => {
    setError(null);

    const archivos = Array.from(fileList || []).filter((f) => EXTENSIONES.test(f.name));

    if (archivos.length === 0) {
      setError("Solo se aceptan imágenes (jpg, png, webp, gif, bmp).");
      return;
    }

    setProgreso({ hechos: 0, total: archivos.length });

    try {
      const { subidas, errores } = await subirFotos(sesionId, archivos, (hechos, total) =>
        setProgreso({ hechos, total })
      );

      if (subidas.length > 0) onSubidas?.();

      if (errores.length > 0) {
        setError(`${errores.length} archivo(s) no se pudieron subir: ${errores.join(" · ")}`);
      }
    } catch (err) {
      setError(err.message || "No se pudieron subir las fotos.");
    } finally {
      setProgreso(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    if (!habilitado) return;
    procesar(e.dataTransfer.files);
  };

  return (
    <div className="sg-panel">
      <h3 className="sg-detail-title">Subir fotos</h3>

      {!sesionId ? (
        <div className="sg-empty">
          <p>Seleccioná una sesión para poder subir fotos.</p>
        </div>
      ) : (
        <>
          <div
            className={`sg-drop${arrastrando ? " sg-drop--active" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (habilitado) setArrastrando(true);
            }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={onDrop}
            onClick={() => habilitado && inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (habilitado) inputRef.current?.click();
              }
            }}
            role="button"
            tabIndex={habilitado ? 0 : -1}
            aria-disabled={!habilitado}
          >
            {progreso ? (
              <Loader2 size={28} className="sg-spin" aria-hidden="true" />
            ) : (
              <Upload size={28} className="sg-drop-icon" aria-hidden="true" />
            )}

            <p className="sg-drop-title">
              {progreso
                ? `Subiendo ${progreso.hechos} de ${progreso.total}...`
                : "Arrastrá las fotos o hacé clic para elegir"}
            </p>
            <p className="sg-drop-hint">JPG, PNG, WEBP, GIF o BMP</p>

            {progreso && (
              <div
                className="sg-progress"
                role="progressbar"
                aria-valuenow={progreso.hechos}
                aria-valuemin={0}
                aria-valuemax={progreso.total}
              >
                <div
                  className="sg-progress-bar"
                  style={{ width: `${Math.round((progreso.hechos / progreso.total) * 100)}%` }}
                />
              </div>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => procesar(e.target.files)}
          />

          {error && (
            <div className="sg-feedback sg-feedback--error" role="alert">
              <span>{error}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FotoUploader;